import { Paper, Stack } from "@mui/material";
import React from "react";
import { CustomModal } from "@cbex/ui/modal";
import { useTranslations } from "next-intl";
import { useWindowDimensions } from "@cbex/utils/hooks";

import {
  CandidatesWithResultsByMomentIdDocument,
  CbexPortalCandidatesWithResultsByMomentIdRow,
  GetCertificateDocumentDataDocument,
  CbexPortalCandidatesWithFiltersRow,
} from "@cbex/data-access";
import dayjs from "dayjs";
import { useLazyQuery, useQuery } from "@apollo/client";
import { downLoadCommunisPDF } from "../utils";
import { useTheme } from "@cbex/utils/theme";
import { CustomTypographyComponent } from "@cbex/ui/text";
import {
  WcDataGrid,
  WcGridActionsCellItem,
  WcGridColDef,
  WcGridRenderCellParams,
  WcGridRowParams,
} from "@bpipartners/datagrid";
import { GrCertificate } from "react-icons/gr";
import { enableDownload } from "../utils/enableDownload";

interface CandidateOverviewModalProps {
  onModalOpen: boolean;
  onModalClose: () => void;
  city: string;
  startDate: string;
  momentID: string;
}

export const CandidateOverviewModal = (props: CandidateOverviewModalProps) => {
  const actionStates = ["PASSED", "FAILED"];

  const { onModalClose, onModalOpen, startDate, city, momentID } = props;
  const t = useTranslations();
  const windowSize = useWindowDimensions();
  const theme = useTheme();

  const [currentCandidate, setCurrentCandidate] =
    React.useState<CbexPortalCandidatesWithFiltersRow>();
  const [candidateData, setCandidateData] = React.useState<
    CbexPortalCandidatesWithResultsByMomentIdRow[]
  >([]);

  const candidateColumnData: WcGridColDef[] = [
    {
      field: "candidateID",
      headerName: t("id"),
    },
    {
      field: "initials",
      headerName: t("labels.initials"),
      minWidth: 80,
      flex: 0.1,
      editable: false,
      sortable: false,
    },
    {
      field: "infix",
      headerName: t("labels.infix"),
      flex: 0.1,
      minWidth: 80,
      sortable: true,
      editable: false,
    },
    {
      field: "lastName",
      headerName: t("labels.lastName"),
      flex: 0.3,
      minWidth: 150,
      sortable: true,
      editable: false,
    },
    {
      field: "activity",
      headerName: t("labels.activity"),
      flex: 0.3,
      minWidth: 150,
      valueGetter: (value, row) => `${row.activity.name || ""} `,
      sortable: true,
      editable: false,
    },
    {
      field: "dateOfBirth",
      headerName: t("labels.dob"),
      flex: 0.2,
      minWidth: 120,
      sortable: false,
      editable: false,
      type: "date",
      disableColumnMenu: true,
      valueGetter: (value) => value && new Date(value),
      valueFormatter: (value) => dayjs(value).format("DD-MM-YYYY"),
    },
    {
      field: "emailAddress",
      headerName: t("labels.email"),
      flex: 0.3,
      minWidth: 80,
      sortable: false,
      editable: false,
    },
    {
      field: "score",
      headerName: t("labels.score"),
      minWidth: 80,
      flex: 0.3,
      sortable: false,
      editable: false,
    },
    {
      field: "result",
      headerName: t("labels.result"),
      minWidth: 80,
      flex: 0.3,
      sortable: false,
      editable: false,
      valueGetter: (value: string) => (value ? t(`value.${value}`) : ""),
    },

    {
      field: "actions",
      type: "actions",
      headerName: t("labels.actions"),
      getActions: (params: WcGridRowParams) => [
        <WcGridActionsCellItem
          key={0}
          disabled={!enableDownload(params.row)}
          icon={
            <GrCertificate
              title={t("labels.downloadCertificate")}
              //@ts-ignore
              color={
                enableDownload(params.row)
                  ? theme.appColors.primary
                  : theme.palette.grey
              }
            />
          }
          label={t("labels.downloadCertificate")}
          onClick={() => handleDownloadClick(params)}
        />,
      ],
    },
  ];

  const { data: candidateDataList, loading: candidateDataListLoading } =
    useQuery(CandidatesWithResultsByMomentIdDocument, {
      variables: {
        momentID: momentID,
      },
      fetchPolicy: "network-only",
    });

  React.useEffect(() => {
    if (
      !candidateDataListLoading &&
      candidateDataList &&
      candidateDataList.CandidatesWithResultsByMomentID
    ) {
      setCandidateData(candidateDataList.CandidatesWithResultsByMomentID);
    } else {
      setCandidateData(undefined);
    }
  }, [candidateDataList, candidateDataListLoading]);

  const [getCertificateDocumentData, { data: CertificateDocumentData }] =
    useLazyQuery(GetCertificateDocumentDataDocument, {
      variables: {
        ids: [],
      },
      fetchPolicy: "network-only",
      //@ts-ignore
    });

  const handleDownloadClick = (params: WcGridRowParams) => {
    setCurrentCandidate(params.row);
    getCertificateDocumentData({
      variables: {
        ids: [params.row.candidateID],
      },
    });
  };

  React.useEffect(() => {
    if (
      CertificateDocumentData &&
      CertificateDocumentData.CertificateDocumentData &&
      CertificateDocumentData.CertificateDocumentData.length > 0
    ) {
      downLoadCommunisPDF({
        downloadName: "cbexCertificate-download",
        pdfObject: {
          ...CertificateDocumentData.CertificateDocumentData[0],
          score: currentCandidate.score,
        },
        templateType:
          currentCandidate.result === "PASSED"
            ? CertificateDocumentData.CertificateDocumentData[0].activity
                .properties.template
            : "examResultFailPDF",
      });
    }
  }, [CertificateDocumentData]);

  const handleModalClose = () => {
    if (onModalClose) {
      onModalClose();
    }
  };

  return (
    <Stack id="CandidateOverviewModal">
      <CustomModal
        modalTitle={
          `${t("labels.currentExam")}` +
          ": " +
          `${dayjs(startDate).format("DD/MM/YYYY HH:mm")} ${city}`
        }
        openModal={onModalOpen}
        closeHandler={handleModalClose}
      >
        <Paper
          sx={{
            height: windowSize.height - 200,
            width: windowSize.width - 400,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {!candidateDataListLoading && candidateData && (
            <WcDataGrid
              getCustomID={(row) => row.candidateID}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    candidateID: false,
                  },
                },
              }}
              columns={candidateColumnData}
              showExport
              rowHeight={60}
              rows={candidateData}
            />
          )}
          {!candidateDataListLoading && candidateData === undefined && (
            <Stack>
              <CustomTypographyComponent variant="body1">
                {t("labels.noCandidates")}
              </CustomTypographyComponent>
            </Stack>
          )}
        </Paper>
      </CustomModal>
    </Stack>
  );
};

export default CandidateOverviewModal;
